import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface OnboardingTemplate {
  id: string;
  onboarding_type: string;
  step_order: number;
  title: string;
  subtitle: string;
  icon_name: string;
  content_blocks: any[];
  is_active: boolean;
}

export const useOnboardingTemplates = (onboardingType?: string) => {
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = async () => {
    setLoading(true);
    let query = supabase
      .from('onboarding_templates')
      .select('*')
      .eq('is_active', true)
      .order('step_order', { ascending: true });

    if (onboardingType) {
      query = query.eq('onboarding_type', onboardingType);
    }

    const { data, error } = await query;
    if (!error && data) {
      setTemplates(data as unknown as OnboardingTemplate[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, [onboardingType]);

  return { templates, loading, refetch: fetchTemplates };
};

export const useOnboardingCheck = (userId?: string | null) => {
  const [pendingOnboarding, setPendingOnboarding] = useState<{
    type: string;
    entityId: string;
    eventName?: string;
  } | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!userId) {
      setChecking(false);
      return;
    }
    checkPending();
  }, [userId]);

  const checkPending = async () => {
    if (!userId) return;
    setChecking(true);

    try {
      // Check if user is a confirmed speaker (has user_id in event_speakers)
      const { data: speakerEntries } = await supabase
        .from('event_speakers')
        .select('event_id, full_name')
        .eq('user_id', userId)
        .not('speaker_type', 'in', '("organizer","co_organizer","lead_organizer")');

      if (speakerEntries && speakerEntries.length > 0) {
        // Check completions
        const { data: completions } = await supabase
          .from('onboarding_completions')
          .select('entity_id')
          .eq('user_id', userId)
          .eq('onboarding_type', 'speaker');

        const completedIds = new Set((completions || []).map((c: any) => c.entity_id));
        const uncompleted = speakerEntries.find(s => !completedIds.has(s.event_id));

        if (uncompleted) {
          // Get event name
          const { data: eventData } = await supabase
            .from('festivals')
            .select('name')
            .eq('id', uncompleted.event_id)
            .single();

          setPendingOnboarding({
            type: 'speaker',
            entityId: uncompleted.event_id,
            eventName: (eventData as any)?.name || 'the event',
          });
          setChecking(false);
          return;
        }
      }

      // Check if user is an approved volunteer
      const { data: volunteerEntries } = await supabase
        .from('volunteer_applications')
        .select('event_id, full_name')
        .eq('user_id', userId)
        .eq('status', 'approved');

      if (volunteerEntries && volunteerEntries.length > 0) {
        const { data: completions } = await supabase
          .from('onboarding_completions')
          .select('entity_id')
          .eq('user_id', userId)
          .eq('onboarding_type', 'volunteer');

        const completedIds = new Set((completions || []).map((c: any) => c.entity_id));
        const uncompleted = volunteerEntries.find(v => !completedIds.has(v.event_id));

        if (uncompleted) {
          const { data: eventData } = await supabase
            .from('festivals')
            .select('name')
            .eq('id', uncompleted.event_id)
            .single();

          setPendingOnboarding({
            type: 'volunteer',
            entityId: uncompleted.event_id,
            eventName: (eventData as any)?.name || 'the event',
          });
          setChecking(false);
          return;
        }
      }

      setPendingOnboarding(null);
    } catch (err) {
      console.error('Error checking onboarding:', err);
    } finally {
      setChecking(false);
    }
  };

  const markComplete = async (type: string, entityId: string) => {
    if (!userId) return;
    await supabase.from('onboarding_completions').upsert({
      user_id: userId,
      onboarding_type: type,
      entity_id: entityId,
    } as any, { onConflict: 'user_id,onboarding_type,entity_id' });
    setPendingOnboarding(null);
  };

  return { pendingOnboarding, checking, markComplete, recheckOnboarding: checkPending };
};
